import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { EmailAttachment } from '../services/email/emailApiService';

export type ParseStatus = 'pending' | 'queued' | 'parsing' | 'completed' | 'error' | 'cancelled' | 'reviewed';

export interface OfferAssignment {
    siteId: string;
    retailerId: string;
    termMonths: number;
}

export interface ParsedOffer {
    id: string;
    attachmentId: string;
    accountId: string;
    fileName: string;
    emailId: string;
    tenderId?: string;  // IMPORTANT: Store tender_id from when document was parsed
    assignment: OfferAssignment;
    status: ParseStatus;
    progress: number;
    parsedData: any | null;
    error?: string;
    /**
     * Non-fatal parse warnings — the offer is usable but something looks wrong, e.g. a
     * charge this retailer states on its other offers that this document did not yield.
     * Previously these only reached the browser console, which is the same as not
     * reporting them: an offer understated by a dropped $782 charge looked complete.
     */
    warnings?: string[];
    reviewedAt?: Date;
    editedData?: any;
    createdAt: Date;
}

interface BatchParsingContextType {
    // State
    selectedAttachments: Map<string, { attachment: EmailAttachment; emailId: string; accountId: string }>;
    assignments: Map<string, OfferAssignment>;
    parsedOffers: ParsedOffer[];
    isParsing: boolean;
    overallProgress: number;

    // Actions
    addAttachment: (attachmentId: string, attachment: EmailAttachment, accountId: string) => void;
    removeAttachment: (attachmentId: string) => void;
    clearAllAttachments: () => void;
    updateAssignment: (attachmentId: string, assignment: OfferAssignment) => void;
    updateBulkAssignment: (assignment: Partial<OfferAssignment>) => void;
    startBatchParsing: (tenderId: string) => Promise<void>;
    startSingleParsing: (attachmentId: string, tenderId: string) => Promise<void>;
    updateParseProgress: (offerId: string, progress: number, status: ParseStatus, data?: any, error?: string, warnings?: string[]) => void;
    markAsReviewed: (offerId: string, editedData?: any) => void;
    /** Persist an in-progress edit without marking the offer reviewed. */
    saveOfferEdits: (offerId: string, editedData?: any) => void;
    getParsedOffer: (offerId: string) => ParsedOffer | undefined;
    retryParsing: (offerId: string) => Promise<void>;
    cancelOffer: (offerId: string) => void;
    cancelAllParsing: () => void;
    resetBatch: () => void;
    setParsedOffers: React.Dispatch<React.SetStateAction<ParsedOffer[]>>;
    setSelectedAttachments: React.Dispatch<React.SetStateAction<Map<string, { attachment: EmailAttachment; emailId: string; accountId: string }>>>;
    setAssignments: React.Dispatch<React.SetStateAction<Map<string, OfferAssignment>>>;
}

const BatchParsingContext = createContext<BatchParsingContextType | undefined>(undefined);

export const useBatchParsing = () => {
    const context = useContext(BatchParsingContext);
    if (!context) {
        throw new Error('useBatchParsing must be used within BatchParsingProvider');
    }
    return context;
};

interface BatchParsingProviderProps {
    children: React.ReactNode;
}

// NOTE: We previously persisted batch parsing state in sessionStorage under a single
// key (`scopingai_batch_parsed_offers`). This caused OLD parsed offers to be
// reloaded even after the user cleared the UI and started a fresh batch.
// To ensure a truly fresh experience for each new batch, we no longer read
// from or write to sessionStorage here. Batch parsing state now lives purely
// in memory for the current Email Processing step.

export const BatchParsingProvider: React.FC<BatchParsingProviderProps> = ({ children }) => {
    const [selectedAttachments, setSelectedAttachments] = useState<Map<string, { attachment: EmailAttachment; emailId: string; accountId: string }>>(new Map());
    const [assignments, setAssignments] = useState<Map<string, OfferAssignment>>(new Map());
    const [parsedOffers, setParsedOffers] = useState<ParsedOffer[]>([]);
    const [isParsing, setIsParsing] = useState(false);
    const [overallProgress, setOverallProgress] = useState(0);
    const abortControllerRef = useRef<AbortController | null>(null);

    const addAttachment = useCallback((attachmentId: string, attachment: EmailAttachment, accountId: string) => {
        setSelectedAttachments(prev => {
            const newMap = new Map(prev);
            newMap.set(attachmentId, {
                attachment,
                emailId: attachment.email_id || '',
                accountId
            });
            return newMap;
        });
    }, []);

    const removeAttachment = useCallback((attachmentId: string) => {
        setSelectedAttachments(prev => {
            const newMap = new Map(prev);
            newMap.delete(attachmentId);
            return newMap;
        });
        setAssignments(prev => {
            const newMap = new Map(prev);
            newMap.delete(attachmentId);
            return newMap;
        });
    }, []);

    const clearAllAttachments = useCallback(() => {
        setSelectedAttachments(new Map());
        setAssignments(new Map());
    }, []);

    const updateAssignment = useCallback((attachmentId: string, assignment: OfferAssignment) => {
        setAssignments(prev => {
            const newMap = new Map(prev);
            newMap.set(attachmentId, assignment);
            return newMap;
        });
    }, []);

    const updateBulkAssignment = useCallback((partialAssignment: Partial<OfferAssignment>) => {
        setAssignments(prev => {
            const newMap = new Map(prev);
            selectedAttachments.forEach((_, attachmentId) => {
                const existing = newMap.get(attachmentId) || { siteId: '', retailerId: '', termMonths: 12 };
                newMap.set(attachmentId, { ...existing, ...partialAssignment });
            });
            return newMap;
        });
    }, [selectedAttachments]);

    const updateParseProgress = useCallback((offerId: string, progress: number, status: ParseStatus, data?: any, error?: string, warnings?: string[]) => {
        setParsedOffers(prev => prev.map(offer =>
            offer.id === offerId
                // warnings ?? offer.warnings, not `warnings || …`: a re-parse that comes back
                // clean must CLEAR a stale warning, and an intermediate progress tick (which
                // passes nothing) must not.
                ? { ...offer, progress, status, parsedData: data || offer.parsedData, error, warnings: warnings ?? offer.warnings }
                : offer
        ));

        // Update overall progress
        setParsedOffers(current => {
            const completed = current.filter(o => o.status === 'completed' || o.status === 'error' || o.status === 'cancelled').length;
            const total = current.length;
            setOverallProgress(total > 0 ? (completed / total) * 100 : 0);
            return current;
        });
    }, []);

    const markAsReviewed = useCallback((offerId: string, editedData?: any) => {
        setParsedOffers(prev => prev.map(offer =>
            offer.id === offerId
                ? { ...offer, status: 'reviewed' as ParseStatus, reviewedAt: new Date(), editedData }
                : offer
        ));
    }, []);

    /**
     * Keep an in-progress edit WITHOUT declaring the offer reviewed.
     *
     * ⚠️ NAVIGATING IS NOT APPROVING.
     *
     * The review screen's flush — what runs when the reviewer clicks another retailer in the
     * list, or leaves step 3 entirely — used to call the same path as "Save & Approve", so the
     * offer you happened to be looking at was stamped `reviewed`, given a reviewedAt, written
     * to the DB and synced, purely because you clicked away from it. Seven retailers browsed
     * once each came out fully "Reviewed" with nobody having checked a single figure, which
     * defeats the point of the status.
     *
     * So the flush lands here instead: the edit is preserved exactly as before, the status is
     * not touched.
     */
    const saveOfferEdits = useCallback((offerId: string, editedData?: any) => {
        setParsedOffers(prev => prev.map(offer =>
            offer.id === offerId
                ? { ...offer, editedData }
                : offer
        ));
    }, []);

    const getParsedOffer = useCallback((offerId: string) => {
        return parsedOffers.find(o => o.id === offerId);
    }, [parsedOffers]);

    const cancelOffer = useCallback((offerId: string) => {
        setParsedOffers(prev => prev.map(offer =>
            offer.id === offerId && (offer.status === 'queued' || offer.status === 'parsing')
                ? { ...offer, status: 'cancelled' as ParseStatus, progress: 0 }
                : offer
        ));
    }, []);

    const cancelAllParsing = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        setParsedOffers(prev => prev.map(offer =>
            (offer.status === 'queued' || offer.status === 'parsing')
                ? { ...offer, status: 'cancelled' as ParseStatus, progress: 0 }
                : offer
        ));

        setIsParsing(false);
    }, []);

    const startBatchParsing = useCallback(async (tenderId: string) => {
        console.log('🔧 Starting batch parsing...');
        console.log(`📌 Using tender_id: ${tenderId}`);

        // Create abort controller
        abortControllerRef.current = new AbortController();

        // Import parsing service dynamically
        const { batchParseOffers } = await import('../services/batchParsingService');

        // Create ParsedOffer entries for all selected attachments
        const offersToParse: ParsedOffer[] = [];
        selectedAttachments.forEach((value, attachmentId) => {
            const assignment = assignments.get(attachmentId);
            if (assignment) {
                offersToParse.push({
                    id: `offer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    attachmentId,
                    accountId: value.accountId,
                    fileName: value.attachment.filename,
                    emailId: value.emailId,
                    tenderId,  // CRITICAL FIX: Store tender_id at parse time
                    assignment,
                    status: 'queued',
                    progress: 0,
                    parsedData: null,
                    createdAt: new Date(),
                });
            }
        });

        console.log(`📦 Created ${offersToParse.length} offers to parse`);

        setParsedOffers(offersToParse);
        setIsParsing(true);
        setOverallProgress(0);

        try {
            console.log('⚡ Calling batchParseOffers...');
            await batchParseOffers(
                offersToParse,
                updateParseProgress,
                abortControllerRef.current.signal
            );
            console.log('✅ Batch parsing completed');
        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
                console.log('⚠️ Batch parsing cancelled by user');
            } else {
                console.error('❌ Batch parsing failed:', error);
            }
        } finally {
            setIsParsing(false);
            abortControllerRef.current = null;
        }
    }, [selectedAttachments, assignments, updateParseProgress]);

    const startSingleParsing = useCallback(async (attachmentId: string, tenderId: string) => {
        const attachmentData = selectedAttachments.get(attachmentId);
        const assignment = assignments.get(attachmentId);

        if (!attachmentData || !assignment) {
            console.error('Cannot start parsing: Attachment or assignment missing');
            return;
        }

        console.log(`📌 Single parsing with tender_id: ${tenderId}`);

        // Create new offer object
        const newOffer: ParsedOffer = {
            id: `offer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            attachmentId,
            accountId: attachmentData.accountId,
            fileName: attachmentData.attachment.filename,
            emailId: attachmentData.emailId,
            tenderId,  // CRITICAL FIX: Store tender_id at parse time
            assignment,
            status: 'queued',
            progress: 0,
            parsedData: null,
            createdAt: new Date(),
        };

        // Add to state
        setParsedOffers(prev => [...prev, newOffer]);

        // Import service
        const { batchParseOffers } = await import('../services/batchParsingService');
        const abortController = new AbortController(); // Individual controller could be managed if needed

        try {
            await batchParseOffers(
                [newOffer],
                updateParseProgress,
                abortController.signal
            );
        } catch (error) {
            console.error('Single parsing failed:', error);
            updateParseProgress(newOffer.id, 0, 'error', undefined, error instanceof Error ? error.message : 'Unknown error');
        }
    }, [selectedAttachments, assignments, updateParseProgress]);

    const retryParsing = useCallback(async (offerId: string) => {
        const offer = parsedOffers.find(o => o.id === offerId);
        if (!offer) return;

        // ⚠️ Clear editedData before re-parsing, or the reparse appears to do nothing.
        //
        // updateParseProgress refreshes `parsedData` with the fresh result, but `editedData`
        // (written by markAsReviewed once an offer has been reviewed) is left untouched — and
        // the review screen prefers editedData when it exists. So the freshly parsed values
        // were being written to state and then hidden behind the previous review's snapshot:
        // a first parse showed the new validity date, a REPARSE of the same offer still showed
        // the old null. The parse itself was correct every time; only the displayed copy was
        // stale.
        //
        // Discarding manual edits here is intentional: a reparse is an explicit request to
        // replace the extracted data, so keeping edits made against the previous extraction
        // would silently reintroduce the same staleness this fixes.
        setParsedOffers(prev => prev.map(o =>
            o.id === offerId ? { ...o, editedData: undefined } : o
        ));

        updateParseProgress(offerId, 0, 'queued');

        const { batchParseOffers } = await import('../services/batchParsingService');
        const abortController = new AbortController();
        await batchParseOffers([offer], updateParseProgress, abortController.signal);
    }, [parsedOffers, updateParseProgress]);

    const resetBatch = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        setSelectedAttachments(new Map());
        setAssignments(new Map());
        setParsedOffers([]);
        setIsParsing(false);
        setOverallProgress(0);
    }, []);

    const value: BatchParsingContextType = {
        selectedAttachments,
        assignments,
        parsedOffers,
        isParsing,
        overallProgress,
        addAttachment,
        removeAttachment,
        clearAllAttachments,
        updateAssignment,
        updateBulkAssignment,
        startBatchParsing,
        startSingleParsing,
        updateParseProgress,
        markAsReviewed,
        saveOfferEdits,
        getParsedOffer,
        retryParsing,
        cancelOffer,
        cancelAllParsing,
        resetBatch,
        setParsedOffers,
        setSelectedAttachments,
        setAssignments,
    };

    return <BatchParsingContext.Provider value={value}>{children}</BatchParsingContext.Provider>;
};
